import React from 'react';
import { Card, Descriptions, Tag, Space, Form, Input, Button, message, Modal } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import request from '../../utils/request';
import { changeMyPassword } from '../../services/auth';
import { useAuth } from '../../hooks/useAuth';

type ProfileData = Record<string, any>;

const formatDateTime = (value: any): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('zh-CN', { hour12: false });
};

const customerSubRoleLabel = (subRole: string | null | undefined): string => {
  if (subRole === 'main_account') return '主账号';
  if (subRole === 'sub_account') return '子账号';
  return '未知';
};

const staffSubRoleLabel = (subRole: string | null | undefined): string => {
  if (subRole === 'warehouse_supervisor') return '仓库主管';
  if (subRole === 'warehouse_staff') return '仓库员工';
  return '未知';
};

const toJoinedText = (value: any): string => {
  if (Array.isArray(value)) return value.length ? value.join('、') : '-';
  if (value == null || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const normalizeLink = (raw: string): string => {
  const text = String(raw || '').trim();
  if (!text) return '';
  if (/^https?:\/\//i.test(text)) return text;
  return `https://${text}`;
};

const renderLinks = (items: string[]) => {
  const list = (items || []).map((x) => String(x || '').trim()).filter(Boolean);
  if (!list.length) return '-';
  return (
    <div>
      {list.map((link, idx) => {
        const href = normalizeLink(link);
        return (
          <div key={`${link}_${href}`} style={{ marginBottom: idx === list.length - 1 ? 0 : 8 }}>
            <a href={href} target="_blank" rel="noreferrer">
              {link}
            </a>
          </div>
        );
      })}
    </div>
  );
};

const renderSocialAccounts = (value: any) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return '-';
  const rows = Object.entries(value)
    .map(([platform, account]) => ({
      platform: String(platform || '').trim(),
      account: String(account || '').trim(),
    }))
    .filter((row) => row.platform || row.account);
  if (!rows.length) return '-';
  return (
    <div>
      {rows.map((row, idx) => (
        <div key={`${row.platform}_${row.account}_${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span>{row.platform || '-'}</span>
          <span>:</span>
          <span>{row.account || '-'}</span>
          {row.account ? (
            <Button
              type="link"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(row.account).then(
                  () => message.success('账号已复制'),
                  () => message.error('复制失败，请检查浏览器权限')
                );
              }}
              style={{ padding: 0, minWidth: 'auto' }}
            />
          ) : null}
        </div>
      ))}
    </div>
  );
};

const emailWithVerifyTag = (email: any, verified: boolean) => (
  <Space size={8}>
    <span>{toJoinedText(email)}</span>
    {verified ? <Tag color="green">已验证</Tag> : <Tag color="red">未验证</Tag>}
  </Space>
);

const getAccountKind = (p: ProfileData) => {
  if (p.role === 'admin') {
    return {
      key: 'admin',
      text: '管理员',
      style: { background: '#e6f4ff', borderColor: '#91caff', color: '#0958d9' },
    };
  }
  if (p.role === 'staff') {
    const subLabel = staffSubRoleLabel(p.staff_sub_role);
    const isSupervisor = p.staff_sub_role === 'warehouse_supervisor';
    return {
      key: `staff_${p.staff_sub_role || 'unknown'}`,
      text: `员工(${subLabel})`,
      style: isSupervisor
        ? { background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d' }
        : { background: '#fff7e6', borderColor: '#ffd591', color: '#d46b08' },
    };
  }
  if (p.role === 'customer') {
    const subLabel = customerSubRoleLabel(p.customer_sub_role);
    const isMain = p.customer_sub_role === 'main_account';
    return {
      key: `customer_${p.customer_sub_role || 'unknown'}`,
      text: `客户(${subLabel})`,
      style: isMain
        ? { background: '#f9f0ff', borderColor: '#d3adf7', color: '#722ed1' }
        : { background: '#fff1f0', borderColor: '#ffa39e', color: '#cf1322' },
    };
  }
  return {
    key: 'unknown',
    text: '未知账号类型',
    style: { background: '#fafafa', borderColor: '#d9d9d9', color: '#595959' },
  };
};

const AccountProfilePage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = React.useState(false);
  const [profile, setProfile] = React.useState<ProfileData | null>(null);
  const [pwdForm] = Form.useForm();
  const [pwdLoading, setPwdLoading] = React.useState(false);
  const [pwdModalOpen, setPwdModalOpen] = React.useState(false);

  const fetchProfile = React.useCallback(async () => {
    setLoading(true);
    try {
      const resp = await request.get('/api/v1/auth/me/');
      setProfile(resp.data || null);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '获取个人资料失败');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const submitChangePassword = async () => {
    try {
      const values = await pwdForm.validateFields();
      setPwdLoading(true);
      const resp = await changeMyPassword({
        current_password: values.current_password,
        new_password: values.new_password,
        confirm_password: values.confirm_password,
      });
      void resp;
      message.success('密码修改成功');
      pwdForm.resetFields();
      setPwdModalOpen(false);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || '密码修改失败');
    } finally {
      setPwdLoading(false);
    }
  };

  const p: any = (profile || user || {}) as any;
  const accountKind = getAccountKind(p);
  const detailItems = React.useMemo(() => {
    if (p.role === 'admin') {
      return [
        { key: 'email', label: '邮箱', children: emailWithVerifyTag(p.email, !!p.email_verified) },
        { key: 'username', label: '用户名', children: toJoinedText(p.username) },
      ];
    }
    if (p.role === 'staff') {
      return [
        { key: 'username', label: '用户名', children: toJoinedText(p.username) },
        {
          key: 'warehouses',
          label: '分配仓库',
          children: Array.isArray(p.assigned_warehouses) && p.assigned_warehouses.length > 0 ? (
            <Space wrap>
              {p.assigned_warehouses.map((w: any) => (
                <Tag key={String(w?.id ?? `${w?.name || ''}_${w?.system_code || ''}`)}>
                  {toJoinedText(w?.name || w?.system_code || w?.id)}
                </Tag>
              ))}
            </Space>
          ) : '-',
        },
      ];
    }
    if (p.role === 'customer' && p.customer_sub_role === 'main_account') {
      const websiteLinks = String(p.website || '')
        .split(/[\n,，;；]+/)
        .map((x: string) => String(x || '').trim())
        .filter(Boolean);
      const salesLinks = Array.isArray(p.sales_platform_links)
        ? p.sales_platform_links.map((x: any) => String(x || '').trim()).filter(Boolean)
        : String(p.sales_platform_links || '')
            .split(/[\n,，;；]+/)
            .map((x: string) => String(x || '').trim())
            .filter(Boolean);
      const socialAccountText = renderSocialAccounts(p.social_accounts);
      return [
        { key: 'customer_code', label: '客户编码', children: toJoinedText(p.customer_code) },
        { key: 'email', label: '邮箱', children: emailWithVerifyTag(p.email, !!p.email_verified) },
        { key: 'username', label: '用户名', children: toJoinedText(p.username) },
        { key: 'company_name', label: '公司名称', children: toJoinedText(p.company_name) },
        { key: 'personal_name', label: '个人姓名', children: toJoinedText(p.personal_name) },
        { key: 'phone_country_code', label: '电话国家码', children: toJoinedText(p.phone_country_code) },
        { key: 'phone_number', label: '电话号码', children: toJoinedText(p.phone_number) },
        { key: 'date_joined', label: '注册时间', children: formatDateTime(p.date_joined) },
        { key: 'website', label: '网站', children: renderLinks(websiteLinks), span: 2 },
        { key: 'main_product_categories', label: '主营品类', children: toJoinedText(p.main_product_categories), span: 2 },
        { key: 'sales_platform_links', label: '销售平台链接', children: renderLinks(salesLinks), span: 2 },
        { key: 'social_accounts', label: '社媒账号', children: socialAccountText, span: 2 },
        { key: 'enabled_business_modules', label: '已开通业务板块', children: toJoinedText(p.enabled_business_modules), span: 2 },
      ];
    }
    if (p.role === 'customer' && p.customer_sub_role === 'sub_account') {
      return [
        { key: 'username', label: '用户名', children: toJoinedText(p.username) },
        { key: 'parent_customer_username', label: '所属主账号', children: toJoinedText(p.parent_customer_username || p.parent_username) },
        { key: 'parent_customer_code', label: '所属主账号客户编码', children: toJoinedText(p.parent_customer_code) },
      ];
    }
    return [{ key: 'empty', label: '信息', children: '-' }];
  }, [p]);

  return (
    <div className="p-4">
      <Card
        title="账户信息"
        loading={loading}
        extra={<Button onClick={fetchProfile} loading={loading}>刷新资料</Button>}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '6px 12px',
            borderRadius: 8,
            border: `1px solid ${accountKind.style.borderColor}`,
            background: accountKind.style.background,
            color: accountKind.style.color,
            fontWeight: 600,
            marginBottom: 14,
          }}
        >
          账户类型：{accountKind.text}
        </div>

        <Descriptions
          className="account-profile-descriptions"
          column={2}
          bordered
          size="small"
          items={detailItems as any}
        />

        <div style={{ marginTop: 14 }}>
          <Button type="primary" onClick={() => setPwdModalOpen(true)}>
            修改密码
          </Button>
        </div>
      </Card>

      <Modal
        title="修改密码"
        open={pwdModalOpen}
        onCancel={() => {
          setPwdModalOpen(false);
          pwdForm.resetFields();
        }}
        onOk={submitChangePassword}
        confirmLoading={pwdLoading}
        okText="确认修改"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={pwdForm} layout="vertical">
          <Form.Item
            label="当前密码"
            name="current_password"
            rules={[{ required: true, message: '请输入当前密码' }]}
          >
            <Input.Password placeholder="请输入当前密码" />
          </Form.Item>
          <Form.Item
            label="新密码"
            name="new_password"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '新密码至少6位' },
            ]}
          >
            <Input.Password placeholder="请输入新密码（至少6位）" />
          </Form.Item>
          <Form.Item
            label="确认新密码"
            name="confirm_password"
            dependencies={['new_password']}
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) return Promise.resolve();
                  return Promise.reject(new Error('两次输入的新密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AccountProfilePage;
